import React, { useState, useEffect } from 'react';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import SquareIntegration from './SquareIntegration';
import DeveloperBottomNav from '../components/DeveloperBottomNav';
import { DSButton, DSCard, DSPageHeader, DSIcon } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './DeveloperTools.css';

function DeveloperTools() {
  const [currentUser, setCurrentUser] = useState(null);
  const [syncingFormats, setSyncingFormats] = useState(false);
  const [syncingMachines, setSyncingMachines] = useState(false);
  const [automationStatus, setAutomationStatus] = useState(null);
  const [kbStatus, setKbStatus] = useState(null);
  const [processing, setProcessing] = useState(null);
  const [matchplaySyncStatus, setMatchplaySyncStatus] = useState(null);
  const [confirmState, setConfirmState] = useState(null); // { title, message, confirmLabel, danger, onConfirm }
  const [confirmSubmitting, setConfirmSubmitting] = useState(false);

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    loadCurrentUser();
  }, []);

  useEffect(() => {
    loadMatchplaySyncStatus();
    if (isAdmin) {
      loadAutomationStatus();
      loadKnowledgeBaseStatus();
    }
  }, [isAdmin]);

  const loadCurrentUser = async () => {
    try {
      const response = await apiService.getCurrentUser();
      setCurrentUser(response.data);
    } catch (err) {
      console.error('Failed to load current user:', err);
    }
  };

  const loadMatchplaySyncStatus = async () => {
    try {
      const response = await apiService.get('/machines/matchplay/sync-status');
      setMatchplaySyncStatus(response.data.lastSync);
    } catch (err) {
      console.error('Failed to load MatchPlay sync status:', err);
    }
  };

  const loadAutomationStatus = async () => {
    try {
      const response = await apiService.get('/automation/status');
      setAutomationStatus(response.data);
    } catch (err) {
      console.error('Failed to load automation status:', err);
    }
  };

  const loadKnowledgeBaseStatus = async () => {
    try {
      const response = await apiService.getKnowledgeBaseStatus();
      setKbStatus(response.data.status || null);
    } catch (err) {
      console.error('Failed to load knowledge base status:', err);
    }
  };

  // Handler for syncing machines from MatchPlay
  const handleMatchPlaySync = () => {
    setConfirmState({
      title: 'Sync Machines from MatchPlay?',
      message: 'Sync machines from MatchPlay? This will import all MatchPlay arenas as machines with OPDB enrichment.',
      confirmLabel: 'Sync',
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          setSyncingMachines(true);
          const response = await apiService.post('/machines/matchplay/sync');
          const { createdCount, updatedCount } = response.data;
          showToast(`Sync complete!\n${createdCount} machines created\n${updatedCount} machines updated`, 'success');
        } catch (err) {
          console.error('Failed to sync machines:', err);
          showToast(err.response?.data?.error || 'Failed to sync with MatchPlay', 'error');
        } finally {
          setConfirmSubmitting(false);
          setSyncingMachines(false);
          setConfirmState(null);
          loadMatchplaySyncStatus();
        }
      }
    });
  };

  const handleWordPressMachineSync = async () => {
    setProcessing('wpsyncmachines');

    try {
      const response = await apiService.post('/automation/sync-wordpress-machines');
      const { updated, failed } = response.data;

      let message = `WordPress machine sync complete: ${updated} updated`;
      if (failed > 0) {
        message += `, ${failed} failed`;
      }

      showToast(message, 'success');
    } catch (err) {
      showToast('Failed to sync machines to WordPress: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleWordPressMachineDryRun = async () => {
    setProcessing('wpsyncmachinesdry');

    try {
      const response = await apiService.post('/automation/sync-wordpress-machines?dryRun=true');
      const { updated, failed } = response.data;

      let message = `WordPress machine dry run complete: ${updated} would update`;
      if (failed > 0) {
        message += `, ${failed} failed`;
      }

      showToast(message, 'success');
    } catch (err) {
      showToast('Failed to run machine sync dry run: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setProcessing(null);
    }
  };

  // The league rules are rendered from the season row on the server and pushed to a WordPress
  // page, so this publishes exactly what members already see in the app. Normally it runs on
  // its own - on a settings change, and nightly - so this button is for publishing a prose edit
  // without waiting for the overnight pass.
  const handlePingolfRulesSync = async (dryRun = false) => {
    setProcessing(dryRun ? 'wppingolfrulesdry' : 'wppingolfrules');

    try {
      const response = await apiService.post(`/automation/sync-wordpress-pingolf-rules${dryRun ? '?dryRun=true' : ''}`);
      const { seasons = 0, failed = 0 } = response.data;

      let message = dryRun
        ? `PinGolf rules dry run complete: ${seasons} active season(s) would publish`
        : `PinGolf rules published for ${seasons} active season(s)`;
      if (failed > 0) {
        message += `, ${failed} failed`;
      }

      showToast(message, failed > 0 ? 'warning' : 'success');
    } catch (err) {
      showToast('Failed to publish PinGolf rules: ' + (err.response?.data?.error || err.message), 'error');
    } finally {
      setProcessing(null);
    }
  };

  const handleKnowledgeBaseSync = () => {
    setConfirmState({
      title: 'Sync Knowledge Base?',
      message: 'Sync knowledge base articles from WordPress now?',
      confirmLabel: 'Sync',
      onConfirm: async () => {
        setProcessing('kbsync');

        try {
          setConfirmSubmitting(true);
          const response = await apiService.syncKnowledgeBase();
          showToast(`Knowledge base sync complete: ${response.data.articles} articles updated`, 'success');
          loadKnowledgeBaseStatus();
        } catch (err) {
          showToast('Failed to sync knowledge base: ' + (err.response?.data?.error || err.message), 'error');
        } finally {
          setConfirmSubmitting(false);
          setProcessing(null);
          setConfirmState(null);
        }
      }
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  // Handler for syncing tournament formats from IFPA API
  const handleSyncTournamentFormats = () => {
    setConfirmState({
      title: 'Sync Tournament Formats?',
      message: 'Sync tournament formats from IFPA API? This will update the cached formats in the database.',
      confirmLabel: 'Sync',
      onConfirm: async () => {
        try {
          setConfirmSubmitting(true);
          setSyncingFormats(true);
          const response = await apiService.post('/ifpa-formats/refresh');
          const total = response.data.qualifyingCount + response.data.finalsCount;
          showToast(`Successfully synced ${total} tournament formats from IFPA! (${response.data.qualifyingCount} qualifying, ${response.data.finalsCount} finals)`, 'success');
        } catch (err) {
          console.error('Failed to sync formats:', err);
          showToast(err.response?.data?.error || 'Failed to sync tournament formats from IFPA API', 'error');
        } finally {
          setConfirmSubmitting(false);
          setSyncingFormats(false);
          setConfirmState(null);
        }
      }
    });
  };

  return (
    <div className="developer-tools bn-page-padding">
      <DSPageHeader title="Developer Tools" />

      <div className="tab-content">
          <DSCard cornerVariant="md">
            <h2>MatchPlay Integration</h2>
            <p className="card-description">
              Sync machines from MatchPlay arena. This will import all MatchPlay arenas as machines with images and metadata from OPDB.
            </p>

            <DSButton
              onClick={handleMatchPlaySync}
              disabled={syncingMachines}
              appearance="solid"
              tone="primary"
              className="developer-tools-btn-full-width"
            >
              {syncingMachines ? <><DSIcon name="refresh" /> Syncing...</> : <><DSIcon name="refresh" /> Sync Machines from MatchPlay</>}
            </DSButton>

            {matchplaySyncStatus && (
              <div className="sync-status-box">
                <div className="sync-status-header">
                  <span className="sync-status-label">Last Sync</span>
                  <StatusBadge variant={matchplaySyncStatus.status === 'success' ? 'success' : 'error'}>
                    {matchplaySyncStatus.status === 'success' ? <><DSIcon name="check" /> Success</> : <><DSIcon name="x" /> Error</>}
                  </StatusBadge>
                </div>
                <div className="sync-status-details">
                  <div><strong>Date:</strong> {new Date(matchplaySyncStatus.syncedAt).toLocaleString()}</div>
                  <div><strong>Created:</strong> {matchplaySyncStatus.machinesCreated} | <strong>Updated:</strong> {matchplaySyncStatus.machinesUpdated}</div>
                  {matchplaySyncStatus.syncedBy && (
                    <div><strong>By:</strong> {matchplaySyncStatus.syncedBy}</div>
                  )}
                  {matchplaySyncStatus.errorMessage && (
                    <div className="sync-error-message">{matchplaySyncStatus.errorMessage}</div>
                  )}
                </div>
              </div>
            )}

            <div className="info-box">
              <strong>Note:</strong> This fetches all arenas from your MatchPlay account and enriches them with manufacturer, year, and images from the OPDB database.
            </div>
          </DSCard>

          <DSCard cornerVariant="md">
            <h2>IFPA Integration</h2>
            <p className="card-description">
              Sync tournament formats from IFPA API to populate the format dropdowns in pinball event creation.
            </p>

            <DSButton
              onClick={handleSyncTournamentFormats}
              disabled={syncingFormats}
              appearance="solid"
              tone="primary"
              className="developer-tools-btn-full-width"
            >
              {syncingFormats ? <><DSIcon name="refresh" /> Syncing from IFPA...</> : <><DSIcon name="refresh" /> Sync IFPA Tournament Formats</>}
            </DSButton>

            <div className="info-box">
              <strong>Note:</strong> This fetches the latest tournament format list from the IFPA API 
              and caches it in the database. Formats include both qualifying and finals formats used in IFPA-sanctioned tournament creation.
            </div>
          </DSCard>

          {isAdmin && (
            <>
              <DSCard cornerVariant="md">
                <h2>WordPress Machine Sync</h2>
                <p className="card-description">
                  Update existing machine pages in WordPress.
                </p>

                <div className="button-group">
                  <DSButton
                    onClick={handleWordPressMachineSync}
                    disabled={processing === 'wpsyncmachines' || !automationStatus?.wordpressApi?.enabled}
                    appearance="solid"
                    tone="primary"
                  >
                    {processing === 'wpsyncmachines' ? <><DSIcon name="refresh" /> Syncing...</> : <><DSIcon name="refresh" /> Sync Machines to WordPress</>}
                  </DSButton>
                  <DSButton
                    onClick={handleWordPressMachineDryRun}
                    disabled={processing === 'wpsyncmachinesdry' || !automationStatus?.wordpressApi?.enabled}
                    appearance="outline"
                    tone="secondary"
                  >
                    {processing === 'wpsyncmachinesdry' ? 'Running...' : 'Dry Run'}
                  </DSButton>
                </div>

                {!automationStatus?.wordpressApi?.enabled && (
                  <p className="warning-text">
                    API sync disabled. Enable WP_API_ENABLED in environment.
                  </p>
                )}
              </DSCard>

              <DSCard cornerVariant="md">
                <h2>PinGolf Rules to WordPress</h2>
                <p className="card-description">
                  Publish each active season's player-facing rules to its WordPress page. The page is
                  rendered from the season's own settings, the same source the app reads, and is
                  republished automatically when season settings change and nightly.
                </p>

                <div className="button-group">
                  <DSButton
                    onClick={() => handlePingolfRulesSync(false)}
                    disabled={processing === 'wppingolfrules' || !automationStatus?.wordpressApi?.enabled}
                    appearance="solid"
                    tone="primary"
                  >
                    {processing === 'wppingolfrules' ? <><DSIcon name="refresh" /> Publishing...</> : <><DSIcon name="refresh" /> Publish Rules to WordPress</>}
                  </DSButton>
                  <DSButton
                    onClick={() => handlePingolfRulesSync(true)}
                    disabled={processing === 'wppingolfrulesdry' || !automationStatus?.wordpressApi?.enabled}
                    appearance="outline"
                    tone="secondary"
                  >
                    {processing === 'wppingolfrulesdry' ? 'Running...' : 'Dry Run'}
                  </DSButton>
                </div>

                {!automationStatus?.wordpressApi?.enabled && (
                  <p className="warning-text">
                    API sync disabled. Enable WP_API_ENABLED in environment.
                  </p>
                )}
              </DSCard>

              <DSCard cornerVariant="md">
                <h2>Knowledge Base Sync</h2>
                <p className="card-description">
                  Pull help articles and categories from WordPress.
                </p>

                <DSButton
                  onClick={handleKnowledgeBaseSync}
                  disabled={processing === 'kbsync'}
                  appearance="solid"
                  tone="primary"
                  className="developer-tools-btn-full-width"
                >
                  {processing === 'kbsync' ? <><DSIcon name="refresh" /> Syncing...</> : <><DSIcon name="refresh" /> Sync Knowledge Base Now</>}
                </DSButton>

                {kbStatus?.completed_at && (
                  <div className="sync-status-box">
                    <div className="sync-status-details">
                      <div><strong>Last Sync:</strong> {formatDate(kbStatus.completed_at)}</div>
                    </div>
                  </div>
                )}
              </DSCard>
            </>
          )}

          <SquareIntegration />
      </div>

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={confirmSubmitting}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}

      <DeveloperBottomNav />
    </div>
  );
}

export default DeveloperTools;
